import { lazy, Suspense } from 'react'

import { defineMessages } from 'react-intl'
import { useLocation } from 'react-router'

import { Page } from '@webapp/core/components/Page'
import { useAppRoutes, useProcurifyIntl } from '@webapp/hooks'
import { parseExportHistoryReferrer } from '../../feature-sets/ReportExportHistoryView'

const ReportExportHistoryViewChunk = lazy(() =>
  import('../../feature-sets/ReportExportHistoryView').then(
    ({ ReportExportHistoryView }) => ({
      default: ReportExportHistoryView,
    })
  )
)

const scope = `procurify.modules.reports.pages.ReportExportHistoryPage`

const messages = defineMessages({
  pageTitle: {
    id: `${scope}.pageTitle`,
    defaultMessage: 'Report export history',
  },
})

export const ReportExportHistoryPage = () => {
  const intl = useProcurifyIntl()
  const { url } = useAppRoutes()
  const location = useLocation()

  // Back-breadcrumb to the originating report, reconstructed from the
  // referrer query param — including the details view when the referrer
  // carried an entity id (BHV-008). `<Page breadcrumbs>` is props-only,
  // so referrer parsing is shared with the Feature Set.
  const { reportConfig, entityId, entityName } = parseExportHistoryReferrer(
    location.search
  )

  const breadcrumbs = [
    ...(reportConfig
      ? [
          {
            link: url(reportConfig.aggregatePage),
            name: intl.formatMessage(reportConfig.titleKey),
          },
          ...(entityId
            ? [
                {
                  link: url(reportConfig.detailPage, {
                    id: String(entityId),
                  }),
                  name: entityName ?? '',
                  escapeCaseFormatting: true,
                },
              ]
            : []),
        ]
      : []),
    { name: intl.formatMessage(messages.pageTitle) },
  ]

  return (
    <Page
      pageTitle={intl.formatMessage(messages.pageTitle)}
      breadcrumbs={breadcrumbs}
    >
      <div data-testid='report-export-history-page'>
        <Suspense fallback={null}>
          <ReportExportHistoryViewChunk />
        </Suspense>
      </div>
    </Page>
  )
}
